import React, { Dispatch, MutableRefObject, SetStateAction, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdOutlineFolder } from 'react-icons/md'
// @ts-ignore - ModernMUITable.js doesn't have TypeScript declarations
import ModernMUITable from '../../../Components/ModernMUITable.js'
import { MobergBoxShadow, MobergColumn, MobergFontSize } from '../../../Moberg';
import { useRecoilState } from 'recoil';
import { annotationGroupsAtom, annotationsAtom, activeAnnotationSessionAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/Annotations';
import { currentPatientFileInfoAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile';
import { useRecoilValue } from "recoil"
import { useModalProvider } from '../../../Providers/ModalProvider';
import { AnnotationSessionManagerModal } from '../ToolBar/Modals/AnnotationSessionManagerModal';
import { MobergAnimationTiming, MobergAnimationCurve, MobergDropdown, MobergRow, MobergButtonShape, MobergIconSize, MobergDropdownSize, MobergButton } from '../../../Moberg';
import { DurationUnit, fromMilliseconds, getFormattedDuration } from '../../../Pages/Data/Visualize/DataReview/Hooks/useDuration';

import { jumpAllWindows } from '../../../Pages/Data/Visualize/DataReview/Atoms/Visualizations';
import { Annotation } from '../Variables/Annotations';
import { DimensionsProvider } from '../../../Providers/DimensionsProvider';

type ActionsBarProps = {
	sidebarWidth: string
	enableSidebar: boolean
	setEnableSidebar: Dispatch<SetStateAction<boolean>>
	actionsBarRef: MutableRefObject<HTMLDivElement>
	isResizing: boolean
}

type AnnotationTableRow = {
	id: number
	start_time: number
	duration: string

}

function ActionsBar(props: ActionsBarProps) {
	const { createModal } = useModalProvider()
	const annotations = useRecoilValue(annotationsAtom)
	const patientInfo = useRecoilValue(currentPatientFileInfoAtom)
	const annotationGroups = useRecoilValue(annotationGroupsAtom)
	const [searchValue, setSearchValue] = useState("")
	const cleanSearchValue = searchValue.trim().toLowerCase()
	const [activeAnnotationSession, setActiveAnnotationSession] = useRecoilState(activeAnnotationSessionAtom)
	const sidebarRef = useRef<HTMLDivElement>(null)
	const sidebarContentRef = useRef<HTMLDivElement>(null)

	const dateFormatter = useMemo(() => {
		const options: Intl.DateTimeFormatOptions & { fractionalSecondDigits: number, hourCycle: string } = {
			timeZone: patientInfo.timeZone,
			year: "numeric",
			month: "short",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			fractionalSecondDigits: 3,
			hourCycle: "h23"
		}
	
		return new Intl.DateTimeFormat("en-us", options)
	}, [patientInfo.timeZone])

	const annotationTableData = annotations
		.filter((annotation: Annotation) => (
			annotation.text.toLowerCase().includes(cleanSearchValue) ||
			annotation.description.toLowerCase().includes(cleanSearchValue) ||
			annotation.tags.some(tag => tag.toLowerCase().includes(cleanSearchValue))
		))
		.map(annotation => ({
			name: annotation.text,
			time: annotation.start_time,
			duration: annotation.end_time - annotation.start_time
		}))

	const writableAnnotationGroups = annotationGroups.filter(group => !group.read_only)

	const dropdownOptions = writableAnnotationGroups.map(group => ({
		label: group.group_name,
		value: group.id
	}))

	const handleDropdownChange = (value: any) => {
		const selectedGroup = writableAnnotationGroups.find(group => group.id === value);
		if (selectedGroup) {
			setActiveAnnotationSession(selectedGroup)
		}
	}

	useLayoutEffect(() => {
		const sidebar = sidebarRef.current
		const sidebarContent = sidebarContentRef.current

		if (!sidebar || !sidebarContent) {
			return
		}

		if (!props.enableSidebar) {
			sidebarContent.style.width = "0px"
			sidebarContent.style.transition = `all ${MobergAnimationCurve.EASE_OUT} ${MobergAnimationTiming.REGULAR}`
		} else {
			sidebarContent.style.width = "400px"
			sidebarContent.style.transition = `all ${MobergAnimationCurve.EASE_IN} ${MobergAnimationTiming.REGULAR}`
		}
	}, [props.enableSidebar, props.sidebarWidth])

	const handleSelectedRow = useCallback((selectedRow: AnnotationTableRow) => {
		const annotation = annotations[selectedRow.id]

		if (!annotation) {
			return
		}

		jumpAllWindows(new Date(annotation.end_time))
	}, [annotations])

	const annotationTableColumns = useMemo(() => [
		{
			field: "time", width: 140, headerName: "Time", visible: true, 
			minWidth: 140, maxWidth: 160,
			renderCell: (params: any) => {
				const formattedDate = dateFormatter.format(params.row.time)
				const parts = formattedDate.split(", ")
				if (parts.length >= 3) {
					const [monthAndDay, year, time] = parts
					return (
						<MobergColumn style={{ fontSize: MobergFontSize.SMALL, lineHeight: "1.6" }}>
							<div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
								{monthAndDay}, {year}
							</div>
							<div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
								{time}
							</div>
						</MobergColumn>
					)
				} else {
					return (
						<MobergColumn style={{ fontSize: MobergFontSize.SMALL }}>
							<div>{formattedDate}</div>
						</MobergColumn>
					)
				}
			},
		},
		{
			field: "name", width: 150, headerName: "Name", visible: true, 
			minWidth: 150, maxWidth: 200,
			renderCell: (params: any) => {
				return (
					<span onMouseDown={() => handleSelectedRow(params.row)} style={{ color: "var(--color-chart-cursor)", cursor: "pointer", fontWeight: "bold", textDecoration: "underline", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", fontSize: MobergFontSize.SMALL }}>
						{params.row.name}
					</span>
				)
			},
		},
		{
			field: "duration", width: 120, headerName: "Duration", visible: true, 
			minWidth: 100, maxWidth: 150,
			renderCell: (params: any) => {
				const duration = fromMilliseconds(params.row.duration, [DurationUnit.SECONDS, DurationUnit.MINUTES])
				return (<span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{getFormattedDuration(duration, { abbreviation: true })}</span>)
			},
		},
	], [dateFormatter, handleSelectedRow])

	return (
		<div ref={sidebarRef} style={{ backgroundColor: "var(--color-surface-panel)", boxShadow: MobergBoxShadow.LOW, borderRadius: "6px", position: "relative", margin: "4px 0px", padding: props.enableSidebar ? "8px" : "8px 2px" }}>
			<MobergRow expand={false} style={{ height: "100%" }} gap="4px">
				<MobergColumn verticalAlign='center'>
					<MobergButton onClick={() => props.setEnableSidebar(previous => !previous)} shape={MobergButtonShape.SQUARE} style={{ padding: "2px" }}>
						{props.enableSidebar
							? <MdChevronRight size={MobergIconSize.REGULAR} />
							: <MdChevronLeft size={MobergIconSize.REGULAR} />
						}
					</MobergButton>
				</MobergColumn>
			<div ref={sidebarContentRef} style={{ height: '100%', width: "0px", overflow: "hidden", transition: `all ${MobergAnimationTiming.REGULAR} ${MobergAnimationCurve.EASE_OUT}` }}>
				<MobergColumn expand={true} gap="16px" style={{ width: "400px", height: "100%" }}>
					<MobergRow expand={false} gap="8px" horizontalAlign="right">
						<MobergDropdown
							label={"Current Annotation Group:"}
							options={dropdownOptions}
							onChange={handleDropdownChange}
							selectedValue={activeAnnotationSession?.id}
							size={MobergDropdownSize.SMALL}
						/>

						<MobergButton onClick={() => createModal(<AnnotationSessionManagerModal />)} shape={MobergButtonShape.SQUARE}>
							<MdOutlineFolder size={MobergIconSize.LARGE} />
						</MobergButton>
					</MobergRow>
					
					<DimensionsProvider>
						<div style={{ position: "relative", height: "calc(100vh - 200px)", width: "100%", overflow: "hidden" }}>
							<ModernMUITable
								tableData={annotationTableData}
								columns={annotationTableColumns}
								checkboxSelection={false}
								isRowSelectable={() => false}
								disableRowSelectionOnClick={true}
								handleOnRowClick={handleSelectedRow}
								onSearchValueChanged={(searchValue: string) => setSearchValue(searchValue)}
								tableToolbarProps={{ 
									searchInput: { isShow: true },
									columnButton: { isShow: false }
								}}
								height="100%"
								scrollbarAlwaysVisible={true}
								disableVirtualization={false}
								overrideStyles={{
									"& .MuiDataGrid-row": {
										lineHeight: "40px",
										minHeight: "40px !important",
									},
									"& .MuiDataGrid-cell": {
										minHeight: "40px !important",
										padding: "4px 8px",
										overflow: "hidden",
									},
									"& .MuiDataGrid-columnHeaders": {
										height: "40px !important",
									},
									"& .MuiDataGrid-footerContainer": {
										height: "52px",
									},
									// Force vertical scrollbar to show, hide horizontal
									"& .MuiDataGrid-virtualScroller": {
										overflowX: "hidden !important",
										overflowY: "auto !important",
										scrollbarWidth: "thin",
									scrollbarColor: "var(--color-scrollbar-thumb) var(--color-scrollbar-track)",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
									width: "8px",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
									background: "var(--color-scrollbar-track)",
									borderRadius: "4px",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
									background: "var(--color-scrollbar-thumb)",
									borderRadius: "4px",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
									background: "var(--color-scrollbar-thumb)",
								},
									"& .MuiDataGrid-virtualScrollerContent": {
										overflow: "visible",
									},
									// Hide horizontal scrollbar
									"& .MuiDataGrid-virtualScrollerRenderZone": {
										overflow: "hidden",
									},
									// Enable column resizing
									"& .MuiDataGrid-columnSeparator": {
										display: "block",
									},
									"& .MuiDataGrid-columnHeader": {
										overflow: "visible",
									},
									"& .MuiDataGrid-columnHeaderResizeZone": {
										display: "block",
									},
									// Ensure scrollbars are always visible
									"& .MuiDataGrid-root": {
										overflow: "auto",
									},
								}}
							/>
						</div>
					</DimensionsProvider>
				</MobergColumn>
			</div>
			</MobergRow>
		</div>
	)
}

export default ActionsBar;
